import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../AuthContext';
import { Attachment, MAX_ATTACHMENTS, fileToAttachment } from '../utils/imageAttachment';
import {
  DRAFT_QUOTE_OBJECT_WORK_ORDERS_QUERY,
  WORK_ORDER_FURNITURE_OBJECT_QUERY,
  ATTACH_BLUEPRINT_IMAGES_TO_WORK_ORDERS_MUTATION,
} from '../graphql/draftQuotes';
import { ALL_WORK_ORDERS_QUERY } from '../graphql/workOrders';
import './Blueprints.css';

interface Vertex {
  id: string;
  x: number;
  y: number;
  z: number;
}

interface Edge {
  from: string;
  to: string;
}

interface BlueprintModel {
  vertices: Vertex[];
  edges: Edge[];
  summary?: string;
}

// Mirrors server/src/blueprintAiCore.ts's Dimensions — the AI's own
// bounding-box measurement of what it actually built (/ai/blueprint's
// `inferredDimensions` field), not a client-side guess.
interface Dimensions {
  width: number;
  depth: number;
  height: number;
}

// Placeholder blueprint shown until this page is wired to real
// AI-generated data (vertices/edges/summary from opencode's save_blueprint
// tool / a future backend query).
const PLACEHOLDER: BlueprintModel = {
  vertices: [
    { id: 't1', x: 0, y: 16, z: 0 },
    { id: 't2', x: 30, y: 16, z: 0 },
    { id: 't3', x: 30, y: 16, z: 18 },
    { id: 't4', x: 0, y: 16, z: 18 },
    { id: 't5', x: 0, y: 17, z: 0 },
    { id: 't6', x: 30, y: 17, z: 0 },
    { id: 't7', x: 30, y: 17, z: 18 },
    { id: 't8', x: 0, y: 17, z: 18 },
    { id: 'l1a', x: 2, y: 0, z: 2 },
    { id: 'l1b', x: 2, y: 16, z: 2 },
    { id: 'l2a', x: 28, y: 0, z: 2 },
    { id: 'l2b', x: 28, y: 16, z: 2 },
    { id: 'l3a', x: 28, y: 0, z: 16 },
    { id: 'l3b', x: 28, y: 16, z: 16 },
    { id: 'l4a', x: 2, y: 0, z: 16 },
    { id: 'l4b', x: 2, y: 16, z: 16 },
  ],
  edges: [
    { from: 't5', to: 't6' }, { from: 't6', to: 't7' },
    { from: 't7', to: 't8' }, { from: 't8', to: 't5' },
    { from: 't1', to: 't2' }, { from: 't2', to: 't3' },
    { from: 't3', to: 't4' }, { from: 't4', to: 't1' },
    { from: 't1', to: 't5' }, { from: 't2', to: 't6' },
    { from: 't3', to: 't7' }, { from: 't4', to: 't8' },
    { from: 'l1a', to: 'l1b' }, { from: 'l2a', to: 'l2b' },
    { from: 'l3a', to: 'l3b' }, { from: 'l4a', to: 'l4b' },
    { from: 'l1b', to: 'l2b' }, { from: 'l2b', to: 'l3b' },
    { from: 'l3b', to: 'l4b' }, { from: 'l4b', to: 'l1b' },
  ],
  summary: 'Sample blueprint — a 30in x 18in x 17in table with four legs. Describe a piece below to replace it.',
};

// The four "look straight at it" camera orientations — hidden-line removal
// is enabled for these plus the 3/4 view below (see matchAxisView's comment
// for why arbitrary free-orbit angles don't get it too). Same theta/phi
// convention as the camera object inside the mount effect: theta = azimuth around Y, phi =
// elevation, PI/2 = level with the horizon. TOP uses phi=0.05 rather than a
// true 0 to match clampCam's pole-avoidance clamp elsewhere in this file —
// the resulting ~3° tilt is imperceptible for occlusion purposes. Shared
// between the live viewer (draw()'s matchAxisView check), the "Save 4 views"
// export (captureFourAngleShots), and the snap-to-view buttons below so all
// four stay numerically identical.
//
// BACK vs FRONT: per blueprintAiCore.ts's coordinate convention, z = 0 is
// the BACK of the piece and z increases toward the FRONT (z = Depth at the
// front edge). cameraWorldPos() (see its comment below) shows theta = 0
// puts the camera at world (0, 0, -radius) — i.e. on the far side of z = 0
// looking toward +z — so that angle is actually looking at the piece's back
// face (the nearest thing to the camera), not its front. theta = PI puts
// the camera on the +z side instead, looking back toward -z, which is what
// actually shows the front (z = Depth) face. This used to be a single
// 'front' entry at theta = 0, mislabeling the back as the front; it's now
// two separate entries so both are available and correctly named.
const AXIS_VIEWS: { name: string; label: string; theta: number; phi: number }[] = [
  { name: 'back', label: 'Back', theta: 0, phi: Math.PI / 2 },
  { name: 'front', label: 'Front', theta: Math.PI, phi: Math.PI / 2 },
  { name: 'side', label: 'Side', theta: Math.PI / 2, phi: Math.PI / 2 },
  { name: 'top', label: 'Top', theta: 0, phi: 0.05 },
];
const VIEW_SNAP_TOLERANCE = 0.02;

// The oblique "see three faces at once" angle a furniture spec sheet's
// isometric-style overview shot would use — the same theta/phi the camera
// starts at on load (see the `cam` object inside the mount effect), so this
// button's framing was already tuned to read well. theta = 0.8 + PI, not the
// original bare 0.8: since theta = 0 looks at the BACK (z = 0) and theta =
// PI looks at the FRONT (z = Depth) — see the AXIS_VIEWS comment above — a
// bare 0.8 (close to 0) leaned toward the back, showing an oblique back
// view when the whole point of a "3/4" shot is to read as a front overview.
// Adding PI is a full 180° spin around the same tilt, landing on the
// equivalent oblique angle from the front side instead.
const THREE_QUARTER_VIEW = { name: 'three-quarter', label: '3/4', theta: Math.PI + 0.8, phi: 1.1 };

// The exact 4 shots "Save 4 views" / "Save 4 + add to work order" produce —
// front, back, the 3/4 overview, and top — deliberately not AXIS_VIEWS
// itself (which is Back/Front/Side/Top, used for the snap buttons and for
// HLR eligibility): this set swaps Side out for the 3/4 view per the shop's
// request, so it needs its own list rather than reusing AXIS_VIEWS as-is.
const FOUR_VIEW_EXPORT: { name: string; label: string; theta: number; phi: number }[] = [
  AXIS_VIEWS.find((v) => v.name === 'front')!,
  AXIS_VIEWS.find((v) => v.name === 'back')!,
  THREE_QUARTER_VIEW,
  AXIS_VIEWS.find((v) => v.name === 'top')!,
];

// A reconstructed axis-aligned bounding box for one connected cluster of
// vertices (a leg, the deck, the back frame...), shrunk inward slightly so a
// point sitting exactly on a box's own face isn't mistaken for occluded by
// its own box. See computeOccluderBoxes inside the mount effect.
interface OccluderBox {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

function formatLen(len: number): string {
  const rounded = Math.round(len * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

// Condenses the structured first message NewBlueprintSession.tsx's
// buildPrompt() sends ("Design a <type>.\nSpecs — Width: 96in, ....\n<notes>")
// into a short title + subtitle chip, matching the mockup's "tap the blue
// spec chip" treatment — tapping it (see addUserMessage below) reveals the
// full prompt text underneath. A freeform follow-up typed straight into the
// chat box won't match this shape at all, and falls back to a plain bubble.
function parseUserChip(text: string): { title: string; subtitle: string } | null {
  const titleMatch = /^Design a ([^.\n]+)\./.exec(text);
  if (!titleMatch) return null;
  const specsMatch = /Specs — ([^.\n]+)\./.exec(text);
  const dimCount = specsMatch ? specsMatch[1].split(',').filter((s) => s.trim()).length : 0;
  const legMatch = /Leg Profile: ([^.\n]+)\./.exec(text);
  const parts: string[] = [];
  if (dimCount > 0) parts.push(`${dimCount} dimension${dimCount === 1 ? '' : 's'}`);
  if (legMatch) parts.push(`leg profile ${legMatch[1].trim()}`);
  return {
    title: `Design a ${titleMatch[1]}`,
    subtitle: parts.join(' · ') || 'no dimensions given',
  };
}

export default function Blueprints() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // The only way into this viewer is via the New Blueprint session form,
  // which hands off the composed prompt through router state. No state
  // means someone navigated here directly (bookmark, refresh, back button)
  // — bounce them to the form instead of showing a blank/placeholder view.
  const initialState = location.state as
    | {
        prompt?: string;
        images?: string[];
        draftQuoteObjectId?: number | null;
        // Set by the "Browse furniture objects" table (NewBlueprintSession.tsx)
        // when a row already has a blueprint_ai_session: resumeModel is that
        // session's last successful turn's return_json, rendered straight
        // into the viewer with no /ai/blueprint call, and resumeSessionId is
        // threaded onto every further prompt so new turns accumulate onto
        // the same session instead of starting a fresh one.
        resumeSessionId?: number;
        resumeModel?: BlueprintModel;
        // That same last turn's own id — threaded into the resumed AI card
        // so its thumbs-down button targets the right row from the start,
        // not just turns generated later in this browser session.
        resumeTurnId?: number;
      }
    | null;
  const initialPrompt = initialState?.prompt;
  const initialImages = initialState?.images || [];
  const resumeSessionId = initialState?.resumeSessionId ?? null;
  const resumeTurnId = initialState?.resumeTurnId ?? null;
  const resumeModel = initialState?.resumeModel ?? null;
  // A session started from scratch (typed straight into the chat, not via
  // a "Design in Blueprints" deep link or the browse table) arrives with no
  // draft object at all — held as state, not a plain const, so the manual
  // "attach to a work order" picker below can set it once the user resolves
  // one after the fact.
  const [draftQuoteObjectId, setDraftQuoteObjectId] = useState<number | null>(
    initialState?.draftQuoteObjectId || null
  );
  const [manualWorkOrderId, setManualWorkOrderId] = useState('');
  const [manualWorkOrderError, setManualWorkOrderError] = useState<string | null>(null);

  // Only fetched when there's no draft object yet to attach to — powers the
  // manual work order picker's <select> options.
  const { data: allWorkOrdersData } = useQuery(ALL_WORK_ORDERS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id || !!draftQuoteObjectId,
  });

  const [fetchWorkOrderFurnitureObject] = useLazyQuery(WORK_ORDER_FURNITURE_OBJECT_QUERY);

  // Resolves a manually-picked work order back to whichever furniture
  // draft_quote_object it carries, then reuses the exact same
  // draftQuoteObjectId-based state/attach flow a deep-linked session already
  // has — see WORK_ORDER_FURNITURE_OBJECT_QUERY's comment for why a work
  // order with no furniture line item can't be attached to at all.
  async function handleSelectWorkOrder(idStr: string) {
    setManualWorkOrderId(idStr);
    setManualWorkOrderError(null);
    if (!idStr) return;
    const id = parseInt(idStr, 10);
    const { data } = await fetchWorkOrderFurnitureObject({ variables: { id } });
    const nodes = data?.workOrderById?.workOrderLineItemsByWorkOrderId?.nodes || [];
    const match = nodes.find(
      (n: any) => n.draftQuoteObjectId != null && String(n.draftQuoteObjectByDraftQuoteObjectId?.itemType).toLowerCase() === 'furniture'
    );
    if (!match) {
      setManualWorkOrderError(`Work order #${id} has no furniture item to attach blueprint images to.`);
      return;
    }
    setDraftQuoteObjectId(match.draftQuoteObjectId);
  }

  const viewRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const promptLogRef = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const verticesBodyRef = useRef<HTMLTableSectionElement>(null);
  const edgesBodyRef = useRef<HTMLTableSectionElement>(null);
  const vertexCountRef = useRef<HTMLSpanElement>(null);
  const edgeCountRef = useRef<HTMLSpanElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The live model lives as a plain closure variable inside the mount-only
  // effect below (see `let model` there) so the canvas draw loop always
  // sees the latest value without re-running the whole effect. The copy
  // buttons are rendered outside that closure, so this ref is how they read
  // whatever the current model actually is at click time.
  const modelRef = useRef<BlueprintModel | null>(null);
  // Snaps the live camera to one of AXIS_VIEWS' exact theta/phi — needed
  // because dragging to hit theta=0/phi=PI/2 by hand is basically impossible,
  // and hidden-line removal below only activates within VIEW_SNAP_TOLERANCE
  // of those exact values. Same ref-mirror pattern as saveCurrentViewRef.
  const snapViewRef = useRef<((theta: number, phi: number) => void) | null>(null);
  // Plain DOM text (not React state) so draw() can update it on every drag
  // frame without triggering a re-render — same reasoning as the
  // vertexCountRef/edgeCountRef pattern below.
  const activeViewLabelRef = useRef<HTMLSpanElement>(null);
  const [copied, setCopied] = useState(false);
  // Vertices/Edges is raw debug data most people never need to look at —
  // closed by default so the chat can use that width instead.
  const [dataOpen, setDataOpen] = useState(false);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // sendPrompt below is defined once (inside the mount-only effect) and reads
  // this ref instead of the `attachments` state directly, so it always sees
  // the latest attachments instead of whatever was current at mount.
  const attachmentsRef = useRef(attachments);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Same ref-mirror pattern as attachments: draw() lives inside the
  // mount-once effect below and reads this ref rather than the state
  // directly (a plain closure over `showMeasurements` would always see the
  // initial value). drawRef lets the toggle below trigger a redraw without
  // re-running the whole effect (which would reset the camera).
  const [showMeasurements, setShowMeasurements] = useState(true);
  const showMeasurementsRef = useRef(showMeasurements);
  const drawRef = useRef<(() => void) | null>(null);
  // Same ref-mirror pattern as drawRef: these live inside the mount-once
  // effect (they need direct access to `cam`/`model`/the canvas) but are
  // triggered from buttons rendered outside that closure.
  const saveCurrentViewRef = useRef<(() => void) | null>(null);
  const saveFourAnglesRef = useRef<(() => void) | null>(null);
  // Populates the vertices/edges tables — same ref-mirror pattern as drawRef.
  // Needed because those tables only exist in the DOM while the panel is
  // open (see `dataOpen`), so expanding it after a model was set while
  // collapsed has nothing to render from until this is re-invoked — see the
  // effect below that calls it on dataOpen flipping true.
  const renderDataListsRef = useRef<((data: BlueprintModel) => void) | null>(null);
  const [savingAngles, setSavingAngles] = useState(false);
  // Non-downloading counterparts of the two refs above: they capture the
  // same frame(s) but hand back the raw dataURL(s) instead of triggering a
  // browser file download, so the "add to work order" buttons below can
  // upload them instead.
  const captureCurrentViewRef = useRef<(() => string) | null>(null);
  const captureFourAngleShotsRef = useRef<(() => { label: string; dataUrl: string }[]) | null>(null);

  // Only set when this session was started from "Design in Blueprints" or
  // the browse table on NewBlueprintSession.tsx — a session with no known
  // draft_quote_object has nothing to attach a saved image to.
  const { data: workOrdersData } = useQuery(DRAFT_QUOTE_OBJECT_WORK_ORDERS_QUERY, {
    variables: { id: draftQuoteObjectId },
    skip: !draftQuoteObjectId,
  });
  // A draft_quote_object's line items can belong to more than one work
  // order (work_order_line_item.draft_quote_object_id is a plain FK, no
  // uniqueness constraint — see CLAUDE.md's Roller Shade section on this
  // same FK) — dedupe and attach to all of them, not just the first.
  const linkedWorkOrders: { id: number; travelerNumber: string | null }[] = Array.from(
    new Map<number, { id: number; travelerNumber: string | null }>(
      (workOrdersData?.draftQuoteObjectById?.workOrderLineItemsByDraftQuoteObjectId?.nodes || [])
        .map((li: any) => li.workOrderByWorkOrderId)
        .filter((wo: any) => wo?.id != null)
        .map((wo: any) => [wo.id, wo])
    ).values()
  );
  const workOrderIds: number[] = linkedWorkOrders.map((wo) => wo.id);
  const draftQuoteObjectName: string | null = workOrdersData?.draftQuoteObjectById?.name || null;

  const [attachImages] = useMutation(ATTACH_BLUEPRINT_IMAGES_TO_WORK_ORDERS_MUTATION);
  const [attachingView, setAttachingView] = useState(false);
  const [attachingAngles, setAttachingAngles] = useState(false);
  const [attachStatus, setAttachStatus] = useState<string | null>(null);

  async function handleSaveAndAttach() {
    const dataUrl = captureCurrentViewRef.current?.();
    if (!dataUrl || !draftQuoteObjectId) return;
    setAttachingView(true);
    setAttachStatus(null);
    try {
      await attachImages({
        variables: { draftQuoteObjectId, images: [{ label: 'view', dataUrl }] },
      });
      setAttachStatus(
        workOrderIds.length > 1
          ? `Added to ${workOrderIds.length} work orders.`
          : 'Added to work order.'
      );
    } catch (err: any) {
      setAttachStatus(`Couldn't save: ${err.message || err}`);
    } finally {
      setAttachingView(false);
    }
  }

  async function handleSaveFourAndAttach() {
    const shots = captureFourAngleShotsRef.current?.();
    if (!shots?.length || !draftQuoteObjectId) return;
    setAttachingAngles(true);
    setAttachStatus(null);
    try {
      await attachImages({
        variables: {
          draftQuoteObjectId,
          images: shots.map((s) => ({ label: s.label, dataUrl: s.dataUrl })),
        },
      });
      setAttachStatus(
        workOrderIds.length > 1
          ? `Added 4 views to ${workOrderIds.length} work orders.`
          : 'Added 4 views to work order.'
      );
    } catch (err: any) {
      setAttachStatus(`Couldn't save: ${err.message || err}`);
    } finally {
      setAttachingAngles(false);
    }
  }
  useEffect(() => {
    showMeasurementsRef.current = showMeasurements;
    drawRef.current?.();
  }, [showMeasurements]);

  // Dims edges by camera-space depth instead of hiding any of them — a
  // parametric build stacks many overlapping boxes (deck, arms, back frame,
  // cushions...), and rendering every edge at full opacity reads as a
  // tangle. Fading far edges lets the near/"leading" geometry read clearly
  // without actually discarding any line. A hard "hide" mode was tried and
  // dropped: the model is edges-only with no face/surface data, so there's
  // no way to test real occlusion — a global depth-midpoint cutoff doesn't
  // approximate what a human would actually see (it can hide part of a near
  // leg's own edges while leaving a stray far edge visible), where fading
  // is at least never actively wrong, just a soft depth cue.
  const [fadeDistantLines, setFadeDistantLines] = useState(false);
  const fadeDistantLinesRef = useRef(fadeDistantLines);
  useEffect(() => {
    fadeDistantLinesRef.current = fadeDistantLines;
    drawRef.current?.();
  }, [fadeDistantLines]);

  // The vertices/edges tables only exist in the DOM while the panel is open
  // (closed by default — see `dataOpen`), so re-populate them from whatever
  // model is already loaded whenever the panel opens, instead of leaving
  // them empty until the next AI response calls renderDataLists itself.
  useEffect(() => {
    if (dataOpen && modelRef.current) renderDataListsRef.current?.(modelRef.current);
  }, [dataOpen]);

  useEffect(() => {
    if (!initialPrompt && !resumeModel) {
      navigate('/blueprints', { replace: true });
      return;
    }
    // Consume the New Blueprint Session hand-off from browser history right
    // away, once this render has already captured it into `initialPrompt`/
    // `initialImages`/`resumeModel` above. Those captured values are what
    // the mount effect below uses to fire the one legitimate auto-send for
    // this hand-off (clicking "Start Session", or clicking a "Resume"
    // browse-table row, IS the explicit user action authorizing it).
    // Clearing history.state here is what keeps that legitimate the ONLY
    // send: history.state (unlike React state) survives a full page reload —
    // e.g. the dev server's own auto-reload after an unrelated file save
    // elsewhere — and without this, a stray remount would find the same
    // prompt still sitting there and silently re-fire it.
    navigate(location.pathname, { replace: true, state: null });
    // Only ever needs to run once, off the prompt this component mounted with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Copies the current model's vertices AND edges together, as the same
  // {vertices, edges} shape used everywhere else in this app (few-shot
  // examples, test cases, "Current blueprint" edit payloads), so it can be
  // pasted straight into any of those.
  async function handleCopyAll() {
    const current = modelRef.current;
    if (!current) return;
    const text = JSON.stringify({ vertices: current.vertices, edges: current.edges }, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail (permissions, non-secure context) — not
      // worth a whole error UI for a copy button; just leave the "Copy"
      // label as-is so the user can tell nothing happened and try again.
    }
  }

  async function addFiles(files: File[]) {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    const toAdd = imageFiles.slice(0, Math.max(0, room));
    const converted = await Promise.all(toAdd.map(fileToAttachment));
    setAttachments((prev) => [...prev, ...converted].slice(0, MAX_ATTACHMENTS));
  }

  async function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await addFiles(files);
  }

  const [dropzoneActive, setDropzoneActive] = useState(false);
  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDropzoneActive(false);
    await addFiles(Array.from(e.dataTransfer.files || []));
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  useEffect(() => {
    const view = viewRef.current!;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;

    // Guard against React 18 StrictMode's dev-only double-invoke of this
    // effect appending the placeholder's intro message twice.
    if (promptLogRef.current) promptLogRef.current.innerHTML = '';

    let model: BlueprintModel | null = null;
    let vertexById: Record<string, Vertex> = {};
    // Recomputed once per setModel() call (not per draw()) — see
    // computeOccluderBoxes below for how these are derived.
    let occluderBoxes: OccluderBox[] = [];

    const cam = {
      theta: THREE_QUARTER_VIEW.theta,
      phi: THREE_QUARTER_VIEW.phi,
      radius: 70,
      target: { x: 0, y: 10, z: 0 },
      fov: 600,
      // Screen-space correction applied on top of the 3D framing below —
      // perspective projection skews near/far corners asymmetrically, so
      // centering the 3D bounding box alone doesn't center it on screen.
      // See recenter().
      offset: { x: 0, y: 0 },
    };

    function clampCam() {
      cam.phi = Math.max(0.05, Math.min(Math.PI - 0.05, cam.phi));
      cam.radius = Math.max(10, Math.min(400, cam.radius));
    }

    function project(p: { x: number; y: number; z: number }) {
      const x = p.x - cam.target.x;
      const y = p.y - cam.target.y;
      const z = p.z - cam.target.z;

      const ct = Math.cos(-cam.theta);
      const st = Math.sin(-cam.theta);
      const x1 = x * ct - z * st;
      const z1 = x * st + z * ct;

      const cp = Math.cos(cam.phi - Math.PI / 2);
      const sp = Math.sin(cam.phi - Math.PI / 2);
      const y1 = y * cp - z1 * sp;
      let z2 = y * sp + z1 * cp;

      z2 += cam.radius;
      if (z2 <= 0.1) return null;

      const w = view.clientWidth;
      const h = view.clientHeight;
      const scale = cam.fov / z2;
      return {
        x: w / 2 + x1 * scale + cam.offset.x,
        y: h / 2 - y1 * scale + cam.offset.y,
        depth: z2,
      };
    }

    // Nudges cam.offset so the model's projected screen-space bounding box
    // (not just its 3D bounding box) sits centered in the viewer, regardless
    // of viewer aspect ratio or how perspective skews the near/far corners.
    function recenter(data: BlueprintModel) {
      cam.offset = { x: 0, y: 0 };
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      let found = false;
      for (const v of data.vertices) {
        const p = project(v);
        if (!p) continue;
        found = true;
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      if (!found) return;
      const w = view.clientWidth;
      const h = view.clientHeight;
      cam.offset = {
        x: w / 2 - (minX + maxX) / 2,
        y: h / 2 - (minY + maxY) / 2,
      };
    }

    // Inverts project()'s rotation to find the camera's actual world-space
    // position (relative to target, then offset by it) — derived by noting
    // the camera always sits at camera-space (0,0,-radius) (that's exactly
    // where z2 hits 0, i.e. the point project() can't render), and project's
    // two rotations are both orthogonal, so the inverse is just applying
    // them transposed/reversed. Verified against the axis-aligned cases by
    // hand: theta=0,phi=PI/2 (BACK) gives (0,0,-radius) — camera behind -Z
    // looking toward +Z, matching project()'s `depth = z + radius`. Since
    // z=0 is the piece's back (see the AXIS_VIEWS comment above), that's the
    // back face nearest the camera at theta=0 — theta=PI (FRONT) instead
    // gives (0,0,+radius), on the model's front (z=Depth) side.
    function cameraWorldPos(): { x: number; y: number; z: number } {
      const ct = Math.cos(-cam.theta);
      const st = Math.sin(-cam.theta);
      const cp = Math.cos(cam.phi - Math.PI / 2);
      const sp = Math.sin(cam.phi - Math.PI / 2);
      return {
        x: cam.target.x - cam.radius * cp * st,
        y: cam.target.y - cam.radius * sp,
        z: cam.target.z - cam.radius * cp * ct,
      };
    }

    // Only activates hidden-line removal when the camera is (or has been
    // snapped, see snapView below) within VIEW_SNAP_TOLERANCE of one of the
    // four axis-aligned AXIS_VIEWS presets or the 3/4 view — genuinely free
    // orbit (anything the user drags to by hand, not one of these five named
    // angles) keeps the older fadeDistantLines behavior instead, since the
    // box-approximated occlusion test below is tuned and spot-checked
    // against these specific angles, not guaranteed to read well at an
    // arbitrary one. Returns the matched view's label (not its internal
    // name) since that's what the "hidden-line view: ___" indicator shows.
    function matchAxisView(theta: number, phi: number): string | null {
      for (const v of [...AXIS_VIEWS, THREE_QUARTER_VIEW]) {
        if (Math.abs(theta - v.theta) < VIEW_SNAP_TOLERANCE && Math.abs(phi - v.phi) < VIEW_SNAP_TOLERANCE) {
          return v.label;
        }
      }
      return null;
    }

    // Reconstructs one axis-aligned box per connected cluster of vertices
    // (a leg, the deck, the back frame...) since the model itself carries no
    // face/surface data (see fadeDistantLines' comment above) — this is what
    // makes real occlusion testing possible at all: a parametric furniture
    // build is a stack of boxes even though only their edges are sent over
    // the wire, so grouping vertices that share an edge and bounding them
    // recovers an approximate solid for each part. Each box is shrunk inward
    // by a small inset (scaled off the model's own size) so a sample point
    // sitting exactly on a box's own face — e.g. the vertex where a visible
    // front edge meets it — doesn't get flagged as self-occluded; a
    // genuinely hidden point (a back-frame edge behind the deck) sits well
    // inside the inset box regardless. A cluster with almost no extent on 2+
    // axes (a leg modeled as a single line, not a box) is skipped — it has
    // no volume to hide anything behind.
    function computeOccluderBoxes(data: BlueprintModel): OccluderBox[] {
      const parent = new Map<string, string>();
      function find(a: string): string {
        let root = a;
        while (parent.get(root) !== root) root = parent.get(root)!;
        let cur = a;
        while (parent.get(cur) !== root) {
          const next = parent.get(cur)!;
          parent.set(cur, root);
          cur = next;
        }
        return root;
      }
      function union(a: string, b: string) {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent.set(ra, rb);
      }
      for (const v of data.vertices) parent.set(v.id, v.id);
      for (const e of data.edges) {
        if (parent.has(e.from) && parent.has(e.to)) union(e.from, e.to);
      }
      const groups = new Map<string, Vertex[]>();
      for (const v of data.vertices) {
        const root = find(v.id);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root)!.push(v);
      }

      let gMinX = Infinity, gMaxX = -Infinity, gMinY = Infinity, gMaxY = -Infinity, gMinZ = Infinity, gMaxZ = -Infinity;
      for (const v of data.vertices) {
        if (v.x < gMinX) gMinX = v.x; if (v.x > gMaxX) gMaxX = v.x;
        if (v.y < gMinY) gMinY = v.y; if (v.y > gMaxY) gMaxY = v.y;
        if (v.z < gMinZ) gMinZ = v.z; if (v.z > gMaxZ) gMaxZ = v.z;
      }
      const modelDiag = Math.sqrt((gMaxX - gMinX) ** 2 + (gMaxY - gMinY) ** 2 + (gMaxZ - gMinZ) ** 2) || 1;
      const inset = Math.max(0.05, modelDiag * 0.004);

      const boxes: OccluderBox[] = [];
      for (const verts of groups.values()) {
        if (verts.length < 2) continue;
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
        for (const v of verts) {
          if (v.x < minX) minX = v.x; if (v.x > maxX) maxX = v.x;
          if (v.y < minY) minY = v.y; if (v.y > maxY) maxY = v.y;
          if (v.z < minZ) minZ = v.z; if (v.z > maxZ) maxZ = v.z;
        }
        const solidAxes = [maxX - minX, maxY - minY, maxZ - minZ].filter((d) => d > inset * 2).length;
        if (solidAxes < 2) continue;
        boxes.push({
          minX: minX + inset, maxX: maxX - inset,
          minY: minY + inset, maxY: maxY - inset,
          minZ: minZ + inset, maxZ: maxZ - inset,
        });
      }
      return boxes;
    }

    // Standard slab-method ray/AABB test. origin+dir*t parametrizes the ray;
    // returns the [enter, exit] t-range where it's inside the box, or null.
    function rayBoxEntryExit(
      origin: { x: number; y: number; z: number },
      dir: { x: number; y: number; z: number },
      box: OccluderBox
    ): { tmin: number; tmax: number } | null {
      let tmin = -Infinity;
      let tmax = Infinity;
      const axes: ('x' | 'y' | 'z')[] = ['x', 'y', 'z'];
      const mins = { x: box.minX, y: box.minY, z: box.minZ };
      const maxs = { x: box.maxX, y: box.maxY, z: box.maxZ };
      for (const ax of axes) {
        const o = origin[ax];
        const d = dir[ax];
        if (Math.abs(d) < 1e-9) {
          if (o < mins[ax] || o > maxs[ax]) return null;
          continue;
        }
        let t1 = (mins[ax] - o) / d;
        let t2 = (maxs[ax] - o) / d;
        if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
        if (t1 > tmin) tmin = t1;
        if (t2 < tmax) tmax = t2;
        if (tmin > tmax) return null;
      }
      return { tmin, tmax };
    }

    // A point is occluded if any box lies strictly between the camera and
    // it — i.e. the [0,1] segment from camPos to point enters some box
    // before t=1 and hasn't fully exited before t=0. camPos itself is t=0.
    // EPS_T needs to be generous, not just "small": two parts that sit flush
    // against each other (an arm's top rail meeting the back frame's corner
    // post, say) share a face, so a sample point right at that seam lands
    // essentially exactly on the NEIGHBORING box's boundary — not just its
    // own (which the inset in computeOccluderBoxes already guards against).
    // Ordinary floating-point noise in rayBoxEntryExit's divisions can then
    // push that neighbor's computed tmin to just under 1, falsely flagging
    // the point as occluded by the very part it's attached to and biting a
    // tiny notch out of the line right at the joint. 1e-4 wasn't enough
    // slack for that; 1e-3 is still visually negligible (camPos-to-point
    // segments here run tens of world units) but well clear of the noise.
    function isPointOccluded(
      point: { x: number; y: number; z: number },
      camPos: { x: number; y: number; z: number },
      boxes: OccluderBox[]
    ): boolean {
      const dir = { x: point.x - camPos.x, y: point.y - camPos.y, z: point.z - camPos.z };
      const EPS_T = 1e-3;
      for (const box of boxes) {
        const hit = rayBoxEntryExit(camPos, dir, box);
        if (hit && hit.tmin < 1 - EPS_T && hit.tmax > EPS_T) return true;
      }
      return false;
    }

    function draw() {
      const w = view.clientWidth;
      const h = view.clientHeight;

      ctx.fillStyle = '#0d2b45';
      ctx.fillRect(0, 0, w, h);

      ctx.strokeStyle = '#1c4468';
      ctx.lineWidth = 1;
      const G = 60;
      const STEP = 10;
      for (let i = -G; i <= G; i += STEP) {
        const a = project({ x: i, y: 0, z: -G });
        const b = project({ x: i, y: 0, z: G });
        const c = project({ x: -G, y: 0, z: i });
        const d = project({ x: G, y: 0, z: i });
        if (a && b) { ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke(); }
        if (c && d) { ctx.beginPath(); ctx.moveTo(c.x, c.y); ctx.lineTo(d.x, d.y); ctx.stroke(); }
      }

      if (!model) return;

      ctx.strokeStyle = '#e8f4ff';
      ctx.lineWidth = 1.5;
      const labels: { x: number; y: number; depth: number; text: string }[] = [];
      const showMeasures = showMeasurementsRef.current;
      const fadeDistant = fadeDistantLinesRef.current;

      // Hidden-line removal only kicks in within VIEW_SNAP_TOLERANCE of one
      // of the four axis-aligned AXIS_VIEWS presets or the 3/4 view (see
      // matchAxisView) — genuine free orbit falls through to the fade
      // behavior below, unchanged.
      const activeView = matchAxisView(cam.theta, cam.phi);
      const useHLR = !!activeView && occluderBoxes.length > 0;
      const camPos = useHLR ? cameraWorldPos() : null;
      const HLR_SAMPLES = 14;
      if (activeViewLabelRef.current) {
        activeViewLabelRef.current.textContent = useHLR ? `hidden-line view: ${activeView!.toUpperCase()}` : '';
      }

      // Project every vertex once up front so we know the model's full
      // depth range before drawing any edge — needed to normalize each
      // edge's depth into a 0 (nearest) .. 1 (farthest) fade factor.
      let minDepth = Infinity;
      let maxDepth = -Infinity;
      const projectedVerts: Record<string, { x: number; y: number; depth: number }> = {};
      if (fadeDistant && !useHLR) {
        for (const v of model.vertices) {
          const p = project(v);
          if (!p) continue;
          projectedVerts[v.id] = p;
          if (p.depth < minDepth) minDepth = p.depth;
          if (p.depth > maxDepth) maxDepth = p.depth;
        }
      }
      const MIN_EDGE_ALPHA = 0.15;

      for (const edge of model.edges) {
        const va = vertexById[edge.from];
        const vb = vertexById[edge.to];
        let anyVisible = true;

        if (useHLR && camPos) {
          // Sample along the edge in world space and stroke only the runs
          // whose sample isn't behind a nearer occluder box — this is what
          // actually removes a back-frame line instead of just dimming it.
          anyVisible = false;
          let inRun = false;
          for (let i = 0; i <= HLR_SAMPLES; i++) {
            const t = i / HLR_SAMPLES;
            const wp = {
              x: va.x + (vb.x - va.x) * t,
              y: va.y + (vb.y - va.y) * t,
              z: va.z + (vb.z - va.z) * t,
            };
            const hidden = isPointOccluded(wp, camPos, occluderBoxes);
            const sp = hidden ? null : project(wp);
            if (sp) {
              anyVisible = true;
              if (!inRun) { ctx.beginPath(); ctx.moveTo(sp.x, sp.y); inRun = true; }
              else ctx.lineTo(sp.x, sp.y);
            } else if (inRun) {
              ctx.stroke();
              inRun = false;
            }
          }
          if (inRun) ctx.stroke();
        } else {
          const a = fadeDistant ? projectedVerts[edge.from] : project(va);
          const b = fadeDistant ? projectedVerts[edge.to] : project(vb);
          if (!a || !b) {
            anyVisible = false;
          } else {
            if (fadeDistant && maxDepth > minDepth) {
              const t = ((a.depth + b.depth) / 2 - minDepth) / (maxDepth - minDepth);
              ctx.globalAlpha = 1 - t * (1 - MIN_EDGE_ALPHA);
            }
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }

        if (!showMeasures || !anyVisible) continue;

        const pa = project(va);
        const pb = project(vb);
        if (!pa || !pb) continue;
        const dx = vb.x - va.x;
        const dy = vb.y - va.y;
        const dz = vb.z - va.z;
        const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
        labels.push({
          x: (pa.x + pb.x) / 2,
          y: (pa.y + pb.y) / 2,
          depth: (pa.depth + pb.depth) / 2,
          text: formatLen(len),
        });
      }

      if (showMeasures && labels.length) {
        labels.sort((p, q) => p.depth - q.depth);
        ctx.font = "10px 'Courier New', monospace";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const placed: { x: number; y: number; w: number }[] = [];
        for (const l of labels) {
          const tw = ctx.measureText(l.text).width + 8;
          const th = 14;
          let collides = false;
          for (const p of placed) {
            if (Math.abs(l.x - p.x) < (tw + p.w) / 2 && Math.abs(l.y - p.y) < th) {
              collides = true;
              break;
            }
          }
          if (collides) continue;
          placed.push({ x: l.x, y: l.y, w: tw });
          ctx.fillStyle = 'rgba(13, 43, 69, 0.85)';
          ctx.fillRect(l.x - tw / 2, l.y - th / 2, tw, th);
          ctx.fillStyle = '#8fd0ff';
          ctx.fillText(l.text, l.x, l.y);
        }
      }

      ctx.fillStyle = '#ffd166';
      for (const v of model.vertices) {
        if (useHLR && camPos && isPointOccluded(v, camPos, occluderBoxes)) continue;
        const p = project(v);
        if (!p) continue;
        const r = Math.max(1.5, Math.min(4, 180 / p.depth));
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    drawRef.current = draw;

    function downloadDataUrl(dataUrl: string, filename: string) {
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      a.click();
    }

    function saveCurrentView() {
      draw();
      downloadDataUrl(canvas.toDataURL('image/png'), 'blueprint-view.png');
    }
    saveCurrentViewRef.current = saveCurrentView;
    captureCurrentViewRef.current = () => {
      draw();
      return canvas.toDataURL('image/png');
    };

    // Temporarily points the camera at a fixed theta/phi, draws, and reads
    // back the resulting frame — then restores whatever the user had been
    // looking at. theta/phi use the same convention as the drag-to-orbit
    // handlers above (theta = azimuth around Y, phi = elevation, PI/2 =
    // level with the horizon).
    function captureAngle(theta: number, phi: number): string {
      const savedTheta = cam.theta;
      const savedPhi = cam.phi;
      cam.theta = theta;
      cam.phi = phi;
      clampCam();
      if (model) recenter(model);
      draw();
      const dataUrl = canvas.toDataURL('image/png');
      cam.theta = savedTheta;
      cam.phi = savedPhi;
      clampCam();
      if (model) recenter(model);
      draw();
      return dataUrl;
    }

    // The four "telling angles" a furniture spec sheet would show: straight
    // front, straight back, the 3/4 oblique overview, and top-down plan —
    // FOUR_VIEW_EXPORT below, so this and the snap-to-view buttons never
    // drift apart. Shared by the local-download composite below and by the
    // "save 4 & add to work order" button (captureFourAngleShotsRef), which
    // wants the 4 shots kept separate rather than composited.
    function captureFourAngleShots(): { label: string; dataUrl: string }[] {
      return FOUR_VIEW_EXPORT.map((v) => ({ label: v.label.toUpperCase(), dataUrl: captureAngle(v.theta, v.phi) }));
    }
    captureFourAngleShotsRef.current = () => (model ? captureFourAngleShots() : []);

    // Composited into one labeled 2x2 sheet rather than four separate
    // downloads, so saving-to-disk is a single file.
    function saveFourAngles() {
      if (!model) return;
      const shots = captureFourAngleShots();

      const tileW = canvas.width;
      const tileH = canvas.height;
      const dpr = window.devicePixelRatio || 1;
      const composite = document.createElement('canvas');
      composite.width = tileW * 2;
      composite.height = tileH * 2;
      const cctx = composite.getContext('2d')!;

      let loaded = 0;
      for (let i = 0; i < shots.length; i++) {
        const shot = shots[i];
        const img = new Image();
        img.onload = () => {
          const col = i % 2;
          const row = Math.floor(i / 2);
          const x = col * tileW;
          const y = row * tileH;
          cctx.drawImage(img, x, y, tileW, tileH);

          cctx.font = `${12 * dpr}px 'Courier New', monospace`;
          const labelW = cctx.measureText(shot.label).width + 16 * dpr;
          cctx.fillStyle = 'rgba(13, 43, 69, 0.85)';
          cctx.fillRect(x + 8 * dpr, y + 8 * dpr, labelW, 22 * dpr);
          cctx.fillStyle = '#8fd0ff';
          cctx.textBaseline = 'middle';
          cctx.fillText(shot.label, x + 16 * dpr, y + 19 * dpr);

          loaded++;
          if (loaded === shots.length) {
            downloadDataUrl(composite.toDataURL('image/png'), 'blueprint-4-views.png');
            setSavingAngles(false);
          }
        };
        img.src = shot.dataUrl;
      }
    }
    saveFourAnglesRef.current = saveFourAngles;

    // Snaps the live camera to an exact AXIS_VIEWS preset (the "Front" /
    // "Side" / "Top" buttons below) — unlike captureAngle, this doesn't
    // restore the prior view afterward; it's meant to actually change what
    // the user is looking at, which is what turns hidden-line removal on.
    function snapView(theta: number, phi: number) {
      cam.theta = theta;
      cam.phi = phi;
      clampCam();
      if (model) recenter(model);
      draw();
    }
    snapViewRef.current = snapView;

    function addRow(tbody: HTMLTableSectionElement, cells: (string | number)[]) {
      const tr = document.createElement('tr');
      for (const c of cells) {
        const td = document.createElement('td');
        td.textContent = String(c);
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }

    function renderDataLists(data: BlueprintModel) {
      const vBody = verticesBodyRef.current;
      if (vBody) {
        vBody.innerHTML = '';
        for (const v of data.vertices) addRow(vBody, [v.id, v.x, v.y, v.z]);
      }
      const eBody = edgesBodyRef.current;
      if (eBody) {
        eBody.innerHTML = '';
        for (const e of data.edges) addRow(eBody, [e.from, e.to]);
      }
      if (vertexCountRef.current) vertexCountRef.current.textContent = `(${data.vertices.length})`;
      if (edgeCountRef.current) edgeCountRef.current.textContent = `(${data.edges.length})`;
    }
    renderDataListsRef.current = renderDataLists;

    function appendImageStrip(msg: HTMLDivElement, images?: string[]) {
      if (!images || !images.length) return;
      const strip = document.createElement('div');
      strip.className = 'bp-chat-msg-images';
      for (const src of images) {
        const img = document.createElement('img');
        img.src = src;
        strip.appendChild(img);
      }
      msg.appendChild(strip);
    }

    // User turn — condensed to a title+subtitle chip when it matches the
    // structured "Design a X.\nSpecs — ...." shape NewBlueprintSession.tsx
    // sends (see parseUserChip); tapping it reveals the full prompt text
    // underneath, matching the mockup's "tap the blue spec chip" cue. A
    // freeform tweak typed straight into the chat box won't match that
    // shape and just renders as a plain bubble, same as before.
    function addUserMessage(text: string, images?: string[]): HTMLDivElement | undefined {
      const log = promptLogRef.current;
      if (!log) return undefined;
      const msg = document.createElement('div');
      msg.className = 'bp-chat-msg bp-chat-msg--user';
      appendImageStrip(msg, images);

      const chip = parseUserChip(text);
      if (chip) {
        msg.classList.add('bp-chat-msg--chip');
        msg.title = 'Click to view the full prompt';
        const titleEl = document.createElement('div');
        titleEl.className = 'bp-chat-chip-title';
        titleEl.textContent = chip.title;
        const subEl = document.createElement('div');
        subEl.className = 'bp-chat-chip-subtitle';
        subEl.textContent = chip.subtitle;
        const fullEl = document.createElement('div');
        fullEl.className = 'bp-chat-chip-full';
        fullEl.textContent = text;
        fullEl.hidden = true;
        msg.appendChild(titleEl);
        msg.appendChild(subEl);
        msg.appendChild(fullEl);
        msg.addEventListener('click', () => { fullEl.hidden = !fullEl.hidden; });
      } else {
        const textEl = document.createElement('div');
        textEl.textContent = text;
        msg.appendChild(textEl);
      }
      log.appendChild(msg);
      log.scrollTop = log.scrollHeight;
      return msg;
    }

    // Small in-progress status row (not a full bubble) — shown while a
    // request is in flight, then removed once addAiCard/an error message
    // replaces it.
    function addThinkingStatus(label: string): HTMLDivElement | undefined {
      const log = promptLogRef.current;
      if (!log) return undefined;
      const row = document.createElement('div');
      row.className = 'bp-chat-status';
      const dot = document.createElement('span');
      dot.className = 'bp-chat-status-dot';
      const textEl = document.createElement('span');
      textEl.textContent = label;
      row.appendChild(dot);
      row.appendChild(textEl);
      log.appendChild(row);
      log.scrollTop = log.scrollHeight;
      return row;
    }

    function addErrorMessage(text: string): HTMLDivElement | undefined {
      const log = promptLogRef.current;
      if (!log) return undefined;
      const msg = document.createElement('div');
      msg.className = 'bp-chat-msg bp-chat-msg--ai bp-chat-msg--error';
      msg.textContent = text;
      log.appendChild(msg);
      log.scrollTop = log.scrollHeight;
      return msg;
    }

    // AI turn — a card with the plain-language summary, a small derived-
    // dimensions table when the response included one (payload.inferredDimensions
    // — real geometry the AI measured off its own vertices, never fabricated
    // here), a warnings row when the server's geometry sanity checks flagged
    // anything (payload.warnings — detectDuplicateVolumes/checkBoxInvariants/
    // etc. in blueprintValidation.ts; labeled "warnings", not "assumptions",
    // since that's what they actually are), and a footer with a generation
    // counter + a button that scrolls/flashes the viewer into view.
    let aiCardCount = 0;
    function addAiCard(summary: string, opts?: { inferredDimensions?: Dimensions; warnings?: string[]; turnId?: number }): HTMLDivElement | undefined {
      const log = promptLogRef.current;
      if (!log) return undefined;
      aiCardCount += 1;

      const card = document.createElement('div');
      card.className = 'bp-chat-msg bp-chat-msg--ai bp-ai-card';

      const bodyEl = document.createElement('div');
      bodyEl.className = 'bp-ai-card-body';
      bodyEl.textContent = summary;
      card.appendChild(bodyEl);

      const dims = opts?.inferredDimensions;
      if (dims) {
        const table = document.createElement('table');
        table.className = 'bp-ai-dims-table';
        (['width', 'depth', 'height'] as const).forEach((key) => {
          const tr = document.createElement('tr');
          const labelTd = document.createElement('td');
          labelTd.className = 'bp-ai-dims-label';
          labelTd.textContent = key[0].toUpperCase() + key.slice(1);
          const valueTd = document.createElement('td');
          valueTd.className = 'bp-ai-dims-value';
          valueTd.textContent = `${formatLen(dims[key])} in`;
          tr.appendChild(labelTd);
          tr.appendChild(valueTd);
          table.appendChild(tr);
        });
        card.appendChild(table);
      }

      const warnings = opts?.warnings;
      if (warnings && warnings.length) {
        const details = document.createElement('details');
        details.className = 'bp-ai-warnings';
        const summaryEl = document.createElement('summary');
        summaryEl.textContent = `⚠ ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`;
        details.appendChild(summaryEl);
        const list = document.createElement('ul');
        for (const w of warnings) {
          const li = document.createElement('li');
          li.textContent = w;
          list.appendChild(li);
        }
        details.appendChild(list);
        card.appendChild(details);
      }

      const footer = document.createElement('div');
      footer.className = 'bp-ai-card-footer';
      const versionEl = document.createElement('span');
      versionEl.textContent = `generated v${aiCardCount}`;
      const viewBtn = document.createElement('button');
      viewBtn.type = 'button';
      viewBtn.className = 'bp-ai-view-link';
      viewBtn.textContent = 'View in viewport';
      viewBtn.addEventListener('click', () => {
        const el = viewRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('bp-viewer-flash');
        setTimeout(() => el.classList.remove('bp-viewer-flash'), 800);
      });
      const actions = document.createElement('div');
      actions.className = 'bp-ai-card-actions';
      actions.appendChild(viewBtn);

      // Only a real, DB-backed turn (a fresh generation, or a resumed
      // session's last turn) gets a thumbs-down button — the placeholder
      // sample card has no turnId and shows none. thumbsDown always POSTs
      // {thumbsDown: true} on the first click; a second click un-flags it
      // (thumbs_down_bool defaults false — "good until explicitly flagged",
      // see migration 086 — so undoing a misclick is worth supporting).
      if (opts?.turnId != null) {
        const turnId = opts.turnId;
        let flagged = false;
        const thumbsBtn = document.createElement('button');
        thumbsBtn.type = 'button';
        thumbsBtn.className = 'bp-ai-thumbs-btn';
        thumbsBtn.title = 'Flag this response as bad';
        thumbsBtn.textContent = '👎';
        thumbsBtn.addEventListener('click', async () => {
          if (thumbsBtn.disabled) return;
          const next = !flagged;
          thumbsBtn.disabled = true;
          try {
            const token = localStorage.getItem('authToken');
            const apiHost = process.env.REACT_APP_API_HOST || 'http://localhost:8080';
            const res = await fetch(`${apiHost}/ai/blueprint/turn/${turnId}/thumbs-down`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ thumbsDown: next }),
            });
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            flagged = next;
            thumbsBtn.classList.toggle('bp-ai-thumbs-btn--active', flagged);
            thumbsBtn.title = flagged ? 'Marked as bad — click to undo' : 'Flag this response as bad';
          } catch {
            // Leave the button's flagged state unchanged so a failed
            // request doesn't silently claim success.
          } finally {
            thumbsBtn.disabled = false;
          }
        });
        actions.appendChild(thumbsBtn);
      }

      footer.appendChild(versionEl);
      footer.appendChild(actions);
      card.appendChild(footer);

      log.appendChild(card);
      log.scrollTop = log.scrollHeight;
      return card;
    }

    function setModel(data: BlueprintModel, announce: boolean, turnId?: number) {
      model = data;
      modelRef.current = data;
      vertexById = {};
      for (const v of data.vertices) vertexById[v.id] = v;
      occluderBoxes = computeOccluderBoxes(data);

      const min = { x: Infinity, y: Infinity, z: Infinity };
      const max = { x: -Infinity, y: -Infinity, z: -Infinity };
      for (const v of data.vertices) {
        (['x', 'y', 'z'] as const).forEach((k) => {
          if (v[k] < min[k]) min[k] = v[k];
          if (v[k] > max[k]) max[k] = v[k];
        });
      }
      cam.target = {
        x: (min.x + max.x) / 2,
        y: (min.y + max.y) / 2,
        z: (min.z + max.z) / 2,
      };
      const dx = max.x - min.x;
      const dy = max.y - min.y;
      const dz = max.z - min.z;
      const diag = Math.sqrt(dx * dx + dy * dy + dz * dz);
      cam.radius = Math.max(diag * 1.8, 30);
      clampCam();
      recenter(data);
      draw();

      renderDataLists(data);

      if (announce && data.summary) {
        addAiCard(data.summary, { turnId });
      }
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = view.clientWidth * dpr;
      canvas.height = view.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (model) recenter(model);
      draw();
    }

    let dragging = false;
    let lastX = 0;
    let lastY = 0;

    function onMouseDown(e: MouseEvent) {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
    }
    function onMouseMove(e: MouseEvent) {
      if (!dragging) return;
      cam.theta += (e.clientX - lastX) * 0.008;
      cam.phi -= (e.clientY - lastY) * 0.008;
      lastX = e.clientX;
      lastY = e.clientY;
      clampCam();
      draw();
    }
    function onMouseUp() {
      dragging = false;
    }
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      cam.radius *= 1 + e.deltaY * 0.001;
      clampCam();
      draw();
    }
    function onTouchStart(e: TouchEvent) {
      if (e.touches.length === 1) {
        dragging = true;
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;
      }
    }
    function onTouchMove(e: TouchEvent) {
      if (e.touches.length === 1 && dragging) {
        cam.theta += (e.touches[0].clientX - lastX) * 0.008;
        cam.phi -= (e.touches[0].clientY - lastY) * 0.008;
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;
        clampCam();
        draw();
      }
    }
    function onTouchEnd() {
      dragging = false;
    }

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('touchstart', onTouchStart);
    canvas.addEventListener('touchmove', onTouchMove);
    canvas.addEventListener('touchend', onTouchEnd);
    window.addEventListener('resize', resize);

    // The window 'resize' listener above only fires for actual browser
    // window resizes — it never fires when `view` (the .bp-viewer flex
    // child) changes size for a purely internal-layout reason, e.g. the
    // chat panel or the vertices/edges data panel expanding/collapsing
    // (bp-chat--wide / bp-data--collapsed). When that happens, canvas.width/
    // height (the backing pixel buffer, only ever set inside resize()) goes
    // stale relative to the canvas's new CSS-rendered size. draw() always
    // paints using the live view.clientWidth/clientHeight, so it only ever
    // touches the buffer's top-left region matching the new size — the
    // leftover strip from the old, larger buffer is never cleared, and the
    // browser then scales that whole (partly stale) buffer down to fit the
    // smaller CSS box, showing a compressed ghost of old content along one
    // edge until the next real window resize happens to fire. A
    // ResizeObserver on `view` itself catches every layout-driven resize,
    // not just window ones, so the buffer and its content stay in sync.
    const viewResizeObserver = new ResizeObserver(() => resize());
    viewResizeObserver.observe(view);

    // Chat -> /ai/blueprint -> viewer. Prototype: no reference photos, no
    // few-shot examples, so treat every reply as a best-effort guess.
    let active = true;
    let aiGeneratedOnce = false;
    let inFlight = false;
    // Set from the first /ai/blueprint response (which creates the session
    // server-side since no sessionId is sent yet) and echoed back on every
    // later call so the whole chat's turns accumulate under one
    // blueprint_ai_session row instead of each call creating its own. A
    // resumed session (see resumeSessionId above) seeds this directly
    // instead, so the very first prompt typed here is already turn N+1 of
    // that existing session rather than turn 0 of a new one.
    let sessionId: number | null = resumeSessionId;

    async function sendPrompt(overrideText?: string, overrideImages?: string[]) {
      if (inFlight) return;
      const input = promptInputRef.current;
      const text = overrideText ?? input?.value.trim();
      if (!text) return;

      const images = overrideText
        ? (overrideImages || []).map((dataUrl) => ({ dataUrl, name: '' }))
        : attachmentsRef.current;
      addUserMessage(text, images.map((a) => a.dataUrl));
      if (!overrideText && input) input.value = '';
      if (!overrideText) setAttachments([]);

      inFlight = true;
      const sendBtnEl = document.getElementById('bp-chat-send') as HTMLButtonElement | null;
      if (sendBtnEl) sendBtnEl.disabled = true;
      if (input) input.disabled = true;
      const thinkingMsg = addThinkingStatus(aiGeneratedOnce ? 'Regenerating draft…' : 'Generating draft…');

      try {
        const token = localStorage.getItem('authToken');
        const apiHost = process.env.REACT_APP_API_HOST || 'http://localhost:8080';
        const res = await fetch(`${apiHost}/ai/blueprint`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            prompt: text,
            images: images.map((a) => a.dataUrl),
            current: aiGeneratedOnce && model ? { vertices: model.vertices, edges: model.edges } : undefined,
            sessionId,
            draftQuoteObjectId,
          }),
        });

        const payload = await res.json();
        if (!active) return;

        if (typeof payload?.sessionId === 'number') sessionId = payload.sessionId;

        if (!res.ok) {
          throw new Error(payload?.error || `Request failed (${res.status})`);
        }

        aiGeneratedOnce = true;
        thinkingMsg?.remove();
        addAiCard(payload.summary, { inferredDimensions: payload.inferredDimensions, warnings: payload.warnings, turnId: payload.turnId });
        setModel({ vertices: payload.vertices, edges: payload.edges, summary: payload.summary }, false);
      } catch (err: any) {
        if (!active) return;
        thinkingMsg?.remove();
        addErrorMessage(`Couldn't generate a blueprint: ${err.message || err}`);
      } finally {
        if (active) {
          inFlight = false;
          if (sendBtnEl) sendBtnEl.disabled = false;
          if (input) input.disabled = false;
        }
      }
    }

    const sendBtn = document.getElementById('bp-chat-send');
    const onSendClick = () => sendPrompt();
    sendBtn?.addEventListener('click', onSendClick);

    function onPromptKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendPrompt();
      }
    }
    promptInputRef.current?.addEventListener('keydown', onPromptKeyDown);

    resize();
    // Clicking "Start Session" on New Blueprint Session IS the explicit user
    // action that should kick off the first AI turn — so it's fired here
    // automatically, same as before. What actually caused the earlier bug
    // (a stray remount re-sending the same prompt) is guarded against by the
    // "consume the hand-off" effect above, which synchronously clears
    // history.state as soon as this render has captured initialPrompt into
    // this closure — a later remount (e.g. the dev server's own auto-reload
    // after an unrelated file save) reads location.state fresh and finds
    // nothing there to resend, since only THIS mount's closure ever held the
    // real value.
    if (resumeModel) {
      // Resuming a browse-table row: render the last turn's blueprint
      // straight away, no /ai/blueprint call — aiGeneratedOnce=true so the
      // next typed prompt goes out as an edit (`current` set) against this
      // model, not a fresh generation.
      aiGeneratedOnce = true;
      setModel(resumeModel, true, resumeTurnId ?? undefined);
    } else if (initialPrompt) {
      sendPrompt(initialPrompt, initialImages);
    } else {
      setModel(PLACEHOLDER, true);
    }

    return () => {
      active = false;
      drawRef.current = null;
      saveCurrentViewRef.current = null;
      saveFourAnglesRef.current = null;
      snapViewRef.current = null;
      captureCurrentViewRef.current = null;
      captureFourAngleShotsRef.current = null;
      renderDataListsRef.current = null;
      sendBtn?.removeEventListener('click', onSendClick);
      promptInputRef.current?.removeEventListener('keydown', onPromptKeyDown);
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('resize', resize);
      viewResizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="bp-page">
      <div className="bp-header">
        <h1 className="bp-title">Blueprints</h1>
      </div>

      <div className="bp-object-context">
        {draftQuoteObjectId ? (
          <>
            Draft Object #{draftQuoteObjectId}
            {draftQuoteObjectName ? ` — "${draftQuoteObjectName}"` : ''}
            {' · Work Orders: '}
            {linkedWorkOrders.length
              ? linkedWorkOrders.map((wo, i) => (
                  <React.Fragment key={wo.id}>
                    {i > 0 && ', '}
                    <Link to={`/work-orders/${wo.id}`} className="bp-object-context-link">
                      #{wo.id}{wo.travelerNumber ? ` (${wo.travelerNumber})` : ''}
                    </Link>
                  </React.Fragment>
                ))
              : 'none yet'}
            {' '}
            <button
              type="button"
              className="bp-object-context-change"
              onClick={() => {
                setDraftQuoteObjectId(null);
                setManualWorkOrderId('');
                setManualWorkOrderError(null);
              }}
            >
              Change
            </button>
          </>
        ) : (
          <>
            No draft object linked yet.{' '}
            <select
              className="bp-wo-select"
              value={manualWorkOrderId}
              onChange={(e) => handleSelectWorkOrder(e.target.value)}
            >
              <option value="">Attach saved views to a work order…</option>
              {(allWorkOrdersData?.allWorkOrders?.nodes || []).map((wo: any) => (
                <option key={wo.id} value={wo.id}>#{wo.id} — {wo.description}</option>
              ))}
            </select>
            {manualWorkOrderError && <span className="bp-wo-error"> {manualWorkOrderError}</span>}
          </>
        )}
      </div>

      <div className="bp-layout">
        {/* Chat lives outside the viewer — it drives the blueprint, it doesn't render it.
            Gets the extra width the Vertices/Edges panel frees up while collapsed. */}
        <div className={`bp-chat${dataOpen ? '' : ' bp-chat--wide'}`}>
          <h2 className="bp-chat-title">Chat</h2>
          <div ref={promptLogRef} className="bp-chat-log" />

          {attachments.length > 0 && (
            <div className="bp-chat-attachments">
              {attachments.map((a, i) => (
                <div key={i} className="bp-chat-attachment">
                  <img src={a.dataUrl} alt={a.name} />
                  <button
                    type="button"
                    className="bp-chat-attachment-remove"
                    aria-label={`Remove ${a.name}`}
                    onClick={() => removeAttachment(i)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFilesSelected}
          />

          {attachments.length < MAX_ATTACHMENTS && (
            <div
              className={`bp-chat-dropzone${dropzoneActive ? ' bp-chat-dropzone--active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDropzoneActive(true); }}
              onDragLeave={() => setDropzoneActive(false)}
              onDrop={handleDrop}
            >
              Drop a reference photo, or click to browse
            </div>
          )}

          <div className="bp-chat-input-row">
            <button
              type="button"
              className="bp-chat-attach"
              title="Attach reference image(s)"
              disabled={attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            >
              <AttachFileIcon fontSize="small" />
              <span className="bp-chat-attach-count">{attachments.length}</span>
            </button>
            <textarea
              ref={promptInputRef}
              className="bp-chat-input"
              placeholder="Describe a piece or a tweak…"
              rows={2}
            />
            <button type="button" id="bp-chat-send" className="bp-chat-send">Send</button>
          </div>
        </div>

        {/* Viewer only ever shows the blueprint — the measurements toggle is a
            view option (like captions on a video), not a source/edit control. */}
        <div ref={viewRef} className="bp-viewer">
          <canvas ref={canvasRef} className="bp-canvas" />
          <div className="bp-view-hint">drag to orbit &middot; scroll to zoom</div>
          <span ref={activeViewLabelRef} className="bp-hlr-indicator" />
          <div className="bp-snap-views">
            {AXIS_VIEWS.map((v) => (
              <button
                key={v.name}
                type="button"
                className="bp-save-btn"
                onClick={() => snapViewRef.current?.(v.theta, v.phi)}
                title={`Snap to the ${v.label} view (hides lines behind the nearer geometry)`}
              >
                {v.label}
              </button>
            ))}
            <button
              type="button"
              className="bp-save-btn"
              onClick={() => snapViewRef.current?.(THREE_QUARTER_VIEW.theta, THREE_QUARTER_VIEW.phi)}
              title="Snap to the 3/4 view (hides lines behind the nearer geometry)"
            >
              {THREE_QUARTER_VIEW.label}
            </button>
          </div>
          <div className="bp-save-controls">
            <button
              type="button"
              className="bp-save-btn"
              onClick={() => saveCurrentViewRef.current?.()}
              title="Save the current camera angle as a PNG"
            >
              Save view
            </button>
            <button
              type="button"
              className="bp-save-btn"
              disabled={savingAngles}
              onClick={() => {
                setSavingAngles(true);
                saveFourAnglesRef.current?.();
              }}
              title="Save front, back, 3/4, and top views as one image"
            >
              {savingAngles ? 'Saving…' : 'Save 4 views'}
            </button>
            {workOrderIds.length > 0 && (
              <>
                <button
                  type="button"
                  className="bp-save-btn bp-attach-btn"
                  disabled={attachingView}
                  onClick={handleSaveAndAttach}
                  title={`Save this view and add it to work order${workOrderIds.length > 1 ? 's' : ''} ${workOrderIds.join(', ')}`}
                >
                  {attachingView ? 'Saving…' : 'Save + add to work order'}
                </button>
                <button
                  type="button"
                  className="bp-save-btn bp-attach-btn"
                  disabled={attachingAngles}
                  onClick={handleSaveFourAndAttach}
                  title={`Save 4 views and add them to work order${workOrderIds.length > 1 ? 's' : ''} ${workOrderIds.join(', ')}`}
                >
                  {attachingAngles ? 'Saving…' : 'Save 4 + add to work order'}
                </button>
              </>
            )}
          </div>
          {attachStatus && <div className="bp-attach-status">{attachStatus}</div>}
          <label className="bp-measure-toggle">
            <input
              type="checkbox"
              checked={showMeasurements}
              onChange={(e) => setShowMeasurements(e.target.checked)}
            />
            measurements
          </label>
          <label className="bp-measure-toggle bp-fade-toggle">
            <input
              type="checkbox"
              checked={fadeDistantLines}
              onChange={(e) => setFadeDistantLines(e.target.checked)}
            />
            fade distant lines
          </label>
        </div>

        <div className={`bp-data${dataOpen ? '' : ' bp-data--collapsed'}`}>
          {dataOpen ? (
            <>
              <div className="bp-data-header">
                <button
                  type="button"
                  className="bp-data-collapse-btn"
                  onClick={() => setDataOpen(false)}
                  title="Hide vertices & edges"
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <button type="button" className="bp-copy-btn" onClick={handleCopyAll}>
                  {copied ? 'Copied!' : 'Copy vertices + edges'}
                </button>
              </div>

              <details className="bp-details">
                <summary>Vertices <span ref={vertexCountRef} /></summary>
                <div className="bp-details-body">
                  <div className="bp-table-wrap">
                    <table className="bp-table">
                      <thead>
                        <tr><th>id</th><th>x</th><th>y</th><th>z</th></tr>
                      </thead>
                      <tbody ref={verticesBodyRef} />
                    </table>
                  </div>
                </div>
              </details>

              <details className="bp-details">
                <summary>Edges <span ref={edgeCountRef} /></summary>
                <div className="bp-details-body">
                  <div className="bp-table-wrap">
                    <table className="bp-table">
                      <thead>
                        <tr><th>from</th><th>to</th></tr>
                      </thead>
                      <tbody ref={edgesBodyRef} />
                    </table>
                  </div>
                </div>
              </details>
            </>
          ) : (
            <button
              type="button"
              className="bp-data-expand-btn"
              onClick={() => setDataOpen(true)}
              title="Show vertices & edges"
            >
              <ChevronLeftIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
